import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader, Package, ArrowRight } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { shopApi } from '../lib/api';
import { Button } from '../components/ui/button';

const ShopPurchaseSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  
  const [status, setStatus] = useState('loading');
  const [purchaseInfo, setPurchaseInfo] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const result = await shopApi.getPurchaseStatus(sessionId);
        setPurchaseInfo(result);
        
        if (result.payment_status === 'paid') {
          setStatus('success');
        } else if (result.status === 'expired') {
          setStatus('expired');
        } else {
          setStatus('pending');
        }
      } catch (error) {
        setStatus('error');
      }
    };

    checkStatus();
    
    // Poll for status updates
    const interval = setInterval(checkStatus, 3000);
    return () => clearInterval(interval);
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#030014]">
      <Navbar />
      
      <main className="max-w-xl mx-auto px-4 py-16">
        <div className="admin-card p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader className="w-16 h-16 mx-auto mb-6 text-[#8B5CF6] animate-spin" />
              <h1 className="text-2xl font-bold mb-2">Processing Your Order</h1>
              <p className="text-gray-400">Please wait while we confirm your purchase...</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-[#00FF9D]/20 flex items-center justify-center">
                <CheckCircle className="w-12 h-12 text-[#00FF9D]" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-[#00FF9D]">Purchase Successful!</h1>
              <p className="text-gray-400 mb-6">
                Thank you for your order. The creator has been notified.
              </p>
              
              {purchaseInfo && (
                <div className="bg-[#0F0B1E] rounded-lg p-4 mb-6 text-left">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Amount Paid</span>
                    <span className="font-bold">
                      ${(purchaseInfo.amount_total / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <div className="flex gap-3">
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="flex-1"
                >
                  Continue Browsing
                </Button>
                <Button
                  onClick={() => navigate('/profile')}
                  className="flex-1 bg-[#00FF9D] hover:bg-[#00FF9D]/80 text-black"
                >
                  <Package className="w-4 h-4 mr-2" />
                  My Purchases
                </Button>
              </div>
            </>
          )}

          {status === 'pending' && (
            <>
              <Loader className="w-16 h-16 mx-auto mb-6 text-yellow-400 animate-spin" />
              <h1 className="text-2xl font-bold mb-2 text-yellow-400">Payment Pending</h1>
              <p className="text-gray-400 mb-6">
                Your payment is being processed. This page will update automatically.
              </p>
            </>
          )}

          {status === 'expired' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-500/20 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-gray-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2">Session Expired</h1>
              <p className="text-gray-400 mb-6">
                This payment session has expired. Please try again.
              </p>
              <Button onClick={() => navigate('/')} className="btn-primary">
                <ArrowRight className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-500/20 flex items-center justify-center">
                <XCircle className="w-12 h-12 text-red-400" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-red-400">Something Went Wrong</h1>
              <p className="text-gray-400 mb-6">
                We couldn't verify your purchase. Please contact support if you were charged.
              </p>
              <Button onClick={() => navigate('/')} variant="outline">
                <ArrowRight className="w-4 h-4 mr-2" />
                Return to Home
              </Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default ShopPurchaseSuccessPage;
